import React, { useState } from "react";
import PropTypes from "prop-types";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { subscribe } from "venuescanner-js/react/helpers";
import SrpContainer from "../../../containers/SrpContainer";
import VenueTypeOptions from "venuescanner-js/react/components/VenueTypeOptions";
import FilterOption from "../FilterOption";
import fadeTransitions from "./fadeTransitions.module.scss";
import keywordsClasses from "./keywords.module.scss";
import FiltersContainer from "../FiltersContainer";

const DEFAULT_VALUE = [];
const StylePick = subscribe({ SrpContainer, FiltersContainer })(
  ({ srpContainer, filtersContainer, ...props }) => {
    const { query } = srpContainer.state;
    const nextQuery = filtersContainer.state.values;

    const currentAttrs = nextQuery.attrs || query.attrs || DEFAULT_VALUE;
    const currentStyles = nextQuery.styles || query.styles || DEFAULT_VALUE;
    const currentKeywords =
      nextQuery.keywords || query.keywords || DEFAULT_VALUE;
    const [newKeyword, setNewKeyword] = useState("");
    const addKeyword = () => {
      filtersContainer.set({
        keywords: [...currentKeywords, newKeyword],
      });
      setNewKeyword("");
    };
    const removeKeyword = (keyword) => {
      filtersContainer.set({
        keywords: currentKeywords.filter((k) => k !== keyword),
      });
    };

    const numberOfFiltersApplied =
      currentAttrs.length + currentStyles.length + currentKeywords.length;
    const hasChanged =
      !!nextQuery.attrs || !!nextQuery.styles || !!nextQuery.keywords;

    return (
      <FilterOption
        label="Features &amp; Styles"
        title="Features"
        reset={
          hasChanged
            ? () => {
                filtersContainer.set({
                  attrs: undefined,
                  styles: undefined,
                  keywords: undefined,
                });
                setNewKeyword(DEFAULT_VALUE);
                srpContainer.patchQuery({
                  attrs: DEFAULT_VALUE,
                  styles: DEFAULT_VALUE,
                  keywords: DEFAULT_VALUE,
                });
                srpContainer.refresh({ replace: true });
              }
            : undefined
        }
        apply={
          hasChanged
            ? async () => {
                await srpContainer.patchQuery({
                  attrs: nextQuery.attrs || currentAttrs,
                  styles: nextQuery.styles || currentStyles,
                  keywords: nextQuery.keywords || currentKeywords,
                });
                filtersContainer.set({
                  attrs: undefined,
                  styles: undefined,
                  keywords: undefined,
                });
                srpContainer.refresh({ replace: true });
              }
            : undefined
        }
        value={numberOfFiltersApplied > 0 ? numberOfFiltersApplied : ""}
        {...props}
      >
        <VenueTypeOptions
          value={currentAttrs}
          onChange={(newAttrs) => filtersContainer.set({ attrs: newAttrs })}
          options={srpContainer.state?.attrs?.map((tag) => tag.name)}
          gaPrefix="srp-filter-attr"
        />

        <h2>Styles</h2>
        <VenueTypeOptions
          value={currentStyles}
          onChange={(newStyles) => filtersContainer.set({ styles: newStyles })}
          options={srpContainer.state?.styles?.map((tag) => tag.name)}
          gaPrefix="srp-filter-style"
        />

        <h2>Keyword</h2>
        <div className={keywordsClasses.KeywordInputContainer}>
          <input
            className={keywordsClasses.KeywordInput}
            placeholder="Search for anything"
            value={newKeyword}
            onChange={({ currentTarget }) => setNewKeyword(currentTarget.value)}
            onKeyPress={({ key }) => {
              if (key === "Enter") addKeyword();
            }}
          />
          <CSSTransition
            in={newKeyword.length > 0}
            classNames={fadeTransitions}
            timeout={300}
            key="newKeywordAdd"
            unmountOnExit
          >
            <button
              className={keywordsClasses.KeywordAddButton}
              onClick={addKeyword}
              data-ga="srp-filter-keyword"
            >
              Add
            </button>
          </CSSTransition>
        </div>
        <div className={keywordsClasses.KeywordList}>
          <TransitionGroup>
            {currentKeywords?.map((k) => (
              <CSSTransition
                classNames={fadeTransitions}
                timeout={300}
                key={`keywords-${k}`}
                unmountOnExit
              >
                <Keyword key={k} label={k} onRemove={() => removeKeyword(k)} />
              </CSSTransition>
            ))}
          </TransitionGroup>
        </div>
      </FilterOption>
    );
  }
);

const Keyword = ({ label, onRemove }) => (
  <button className={keywordsClasses.Keyword} onClick={onRemove}>
    {label}
    <span>Remove</span>
  </button>
);

Keyword.propTypes = {
  label: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default StylePick;
